import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import {
  useState, useMemo, useCallback,
} from 'react';
import clsx from 'clsx';
import _ from 'lodash';
import Filter from '../../../../Filters/Filter';
import TabPanel from '../../../../TabPanel';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { FlightSearchTypes } from '../../../../../constants';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';
import {
  sliderToTimeWindow,
  timeWindowToSlider,
} from '../../../../../helpers/flightTimeWindow';
import getFlightRouteLabel from '../../../../../helpers/getFlightRouteLabel';
import TimeSlider from './TimeSlider';

const FlightTimeSlidersInput = ({ tripId, variant, filterBgColor }) => {
  const isTripSearch = !tripId;
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));
  const [selectedTab, setSelectedTab] = useState(0);

  const isOneWay = searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY;
  const isMultiCity = searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY;
  const isDirectSell = searchInputs.flightSearchType === FlightSearchTypes.DIRECT_SELL;

  const isFilterEmpty = useMemo(() => {
    if (!searchInputs.flightSegments) return true;
    return !_.find(
      searchInputs.flightSegments,
      (fs) => fs.departureTimeWindow || fs.arrivalTimeWindow,
    );
  }, [searchInputs.flightSegments]);

  const getSegmentLabel = useCallback((index) => {
    if (isMultiCity) return `Flight ${index + 1}`;
    return index === 0 ? 'Outbound' : 'Return';
  }, [isMultiCity]);

  const getRouteLabel = useCallback((index) => getFlightRouteLabel({
    flightSegments: searchInputs.flightSegments,
    flightSearchType: searchInputs.flightSearchType,
    segmentIndex: index,
  }), [searchInputs.flightSegments, searchInputs.flightSearchType]);

  const updateSegment = useCallback((segmentIndex, updates) => {
    const payload = {
      tripId,
      searchObj: {
        flightSegments: searchInputs.flightSegments
          ? [...searchInputs.flightSegments] : [],
      },
    };

    payload.searchObj.flightSegments[segmentIndex] = {
      ...payload.searchObj.flightSegments[segmentIndex],
      ...updates,
    };

    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
  }, [dispatch, isTripSearch, searchInputs.flightSegments, tripId]);

  const handleDepartureCommit = useCallback((segmentIndex) => (event, newValue) => {
    const departureTimeWindow = sliderToTimeWindow(newValue[0], newValue[1]);
    updateSegment(segmentIndex, { departureTimeWindow });
    track('flightTimeSlidersInput.departureChange', {
      segmentIndex,
      departureTimeWindow,
    });
  }, [updateSegment, track]);

  const handleArrivalCommit = useCallback((segmentIndex) => (event, newValue) => {
    const arrivalTimeWindow = sliderToTimeWindow(newValue[0], newValue[1]);
    updateSegment(segmentIndex, { arrivalTimeWindow });
    track('flightTimeSlidersInput.arrivalChange', {
      segmentIndex,
      arrivalTimeWindow,
    });
  }, [updateSegment, track]);

  const handleClear = useCallback(() => {
    const payload = {
      tripId,
      searchObj: {
        flightSegments: searchInputs.flightSegments
          ? [...searchInputs.flightSegments] : [],
      },
    };

    payload.searchObj.flightSegments.forEach((segment, index) => {
      payload.searchObj.flightSegments[index] = {
        ...payload.searchObj.flightSegments[index],
        departureTimeWindow: '',
        arrivalTimeWindow: '',
      };
    });

    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('flightTimeSlidersInput.clear');
  }, [dispatch, isTripSearch, searchInputs.flightSegments, track, tripId]);

  const handleTabChange = (index) => {
    setSelectedTab(index);
    track('flightTimeSlidersInput.tabChange', { index });
  };

  const handleClose = () => {
    setSelectedTab(0);
  };

  if (!searchInputs || !searchInputs.flightSegments || !searchInputs.flightSegments.length) {
    return <></>;
  }

  // Determine how many tabs to show
  const segmentCount = isOneWay || isDirectSell
    ? 1
    : searchInputs.flightSegments.length;

  const renderSegmentSliders = (segmentIndex) => {
    const segment = searchInputs.flightSegments[segmentIndex] || {};
    const depValue = timeWindowToSlider(segment.departureTimeWindow);
    const arrValue = timeWindowToSlider(segment.arrivalTimeWindow);
    const routeLabel = getRouteLabel(segmentIndex);
    const isReturnSegment = !isMultiCity && segmentIndex > 0;

    return (
      <div>
        {routeLabel && (
          <div className="text-sm text-gray-700 mb-3 font-medium">
            {routeLabel}
          </div>
        )}
        <TimeSlider
          type="departure"
          value={depValue}
          onChangeCommitted={handleDepartureCommit(segmentIndex)}
          isReturn={isReturnSegment}
        />
        <TimeSlider
          type="arrival"
          value={arrValue}
          onChangeCommitted={handleArrivalCommit(segmentIndex)}
          isReturn={isReturnSegment}
        />
      </div>
    );
  };

  return (
    <Filter
      label="Time Preference"
      menuWidthClass="w-max"
      isFilterEmpty={isFilterEmpty}
      onClose={handleClose}
      disableCloseOnOutsideClick
      variant={isTripSearch ? variant : 'outline'}
      filterMaxWidthClass="max-w-full"
      bgClassName={!isTripSearch ? '' : filterBgColor}
      withClear={false}
    >
      <div>
        {segmentCount > 1 && (
          <div className={clsx(
            'border-b-2 grid w-full',
            segmentCount <= 2 ? 'grid-cols-2' : `grid-cols-${segmentCount}`,
          )}
          >
            {Array.from({ length: segmentCount }, (unused, index) => (
              <a
                key={index.toString()}
                onClick={() => handleTabChange(index)}
                onKeyDown={() => handleTabChange(index)}
                role="button"
                tabIndex={index}
                className={clsx(
                  selectedTab === index && 'text-theme border-b-2 border-theme',
                  'text-center py-2 px-5 text-gray-700 cursor-pointer',
                )}
              >
                {getSegmentLabel(index)}
              </a>
            ))}
          </div>
        )}
        <div className="p-5" style={{ minWidth: '320px' }}>
          {segmentCount > 1
            ? Array.from({ length: segmentCount }, (unused, index) => (
              <TabPanel value={selectedTab} index={index} key={index.toString()}>
                {renderSegmentSliders(index)}
              </TabPanel>
            ))
            : renderSegmentSliders(0)}
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={handleClear}
              className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer focus:outline-none"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </Filter>
  );
};

FlightTimeSlidersInput.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterBgColor: PropTypes.string,
};

FlightTimeSlidersInput.defaultProps = {
  tripId: undefined,
  variant: undefined,
  filterBgColor: undefined,
};

export default FlightTimeSlidersInput;
